import { differenceInMilliseconds } from 'date-fns';
import { compact, uniq } from 'lodash-es';
import { useMemo } from 'react';
import type { QuoteStatusColumnParams } from '../components/BlotterTable/columns/columns';
import { getAgGridColId } from '../components/BlotterTable/columns/getAgGridColId';
import type { Column, ColumnDef } from '../components/BlotterTable/columns/types';
import { useDefaultColumns } from '../components/BlotterTable/useDefaultColumns';
import { toBigWithDefault } from '../utils';
import { EMPTY_ARRAY } from '../utils/empty';
import { includesEnumValue } from '../utils/enumlikeHandlers';
import { getEntitySubAccountNames, type AllocationBE } from './Allocation';
import type { CareOrder } from './CareOrder';
import type { ConfirmStructs, ConfirmTypeEnum } from './Confirm';
import { getFixingIndicesList, type FixingDetails } from './FixingDetails';
import type { NegotiationParameters } from './NegotiationParameters';
import { getQuoteLegRepresentatives, type QuoteLegRepresentative } from './QuoteLegRepresentative';
import {
  ParentOrderTypeEnum,
  PricingModeEnum,
  QuoteStatusEnum,
  RFQTradingProtocolEnum,
  SideEnum,
  type ExpressedOutcomeEnum,
  type IOMSQuote,
  type IOMSQuote4401Aggregations,
  type LegDirectionEnum,
  type NegotiationStatusEnum,
  type OrdRejReasonEnum,
} from './types';

export type { QuoteLegRepresentative } from './QuoteLegRepresentative';

export function getQuotePropertyForSide<T>(
  side: SideEnum | undefined,
  { bid, offer, twoWay }: { bid: T; offer: T; twoWay?: T }
): T | undefined {
  if (!side) {
    return undefined;
  }

  if (side === SideEnum.Buy) {
    return offer;
  }
  if (side === SideEnum.Sell) {
    return bid;
  }
  return twoWay;
}

export function getQuoteQtyForSide(side: SideEnum, { BidQty, OfferQty }: Pick<QuoteMarket, 'BidQty' | 'OfferQty'>) {
  return getQuotePropertyForSide(side, { bid: BidQty, offer: OfferQty, twoWay: BidQty });
}

export function isQuote(entity: any): entity is Quote {
  return entity instanceof Quote;
}

const TERMINAL_QUOTE_STATUSES = [
  QuoteStatusEnum.Canceled,
  QuoteStatusEnum.Rejected,
  QuoteStatusEnum.Filled,
  QuoteStatusEnum.PendingFix,
] as const;
const FILLED_QUOTE_STATUSES = [QuoteStatusEnum.Filled, QuoteStatusEnum.PendingFix] as const;

/** Returns true if the given QuoteStatus is a terminal status (Canceled, Rejected, Filled, PendingFix). */
export function isTerminalQuoteStatus(status: QuoteStatusEnum): boolean {
  return includesEnumValue(TERMINAL_QUOTE_STATUSES, status);
}

/** Returns true once a quote or quote market is filled. PendingFix has traded but awaits final fixing. */
export function isFilledQuoteStatus(status: QuoteStatusEnum): boolean {
  return includesEnumValue(FILLED_QUOTE_STATUSES, status);
}

export function canExecuteOtherUserNegotiatedRFQ({
  rfqTradingProtocol,
  enableExecutingOtherUserNegotiatedRFQ,
}: {
  rfqTradingProtocol: RFQTradingProtocolEnum | undefined;
  enableExecutingOtherUserNegotiatedRFQ: boolean;
}): boolean {
  return enableExecutingOtherUserNegotiatedRFQ && rfqTradingProtocol === RFQTradingProtocolEnum.Negotiated;
}

/**
 * Determines if the price (BidPx/OfferPx) of a QuoteMarket is a "valid" price. For example, the backend sends BidPx: "0" when QuoteStatus is PendingNew.
 * 0 is not a valid price in this case. There has been no response yet. Similarly for Rejected, whatever price there is is not valid.
 */
export function isValidQuotePrice(
  price: string | undefined,
  quoteMarketStatus: QuoteStatusEnum | undefined,
  pricingMode: PricingModeEnum | undefined
) {
  if (price == null || quoteMarketStatus == null) {
    return false;
  }

  if (quoteMarketStatus === QuoteStatusEnum.PendingNew || quoteMarketStatus === QuoteStatusEnum.Rejected) {
    return false;
  }

  // Safety guard: QuoteStatus checks should usually be enough, but keep this to prevent placeholder "0"
  // prices from being treated as executable for non-SpreadToFixing quotes.
  if (pricingMode !== PricingModeEnum.SpreadToFixing && price === '0') {
    return false;
  }

  return true;
}

/**
 * Given a QuoteMarket, will look at its Legs and return any Leg Markets which are not the same as QuoteMarket.Market.
 * This means that the returned array of market names are the cross markets for the provided QuoteMarket
 */
export function quoteMarketCrossLegMarkets(quote: Quote, quoteMarket: QuoteMarket | undefined): string[] {
  if (!quoteMarket?.LegIndexes || !quote.Legs) {
    return [];
  }

  return compact(
    quoteMarket.LegIndexes.map(({ LegIndex }) => {
      const legMarket = quote.Legs?.at(LegIndex)?.Market;
      return legMarket !== quoteMarket.Market ? legMarket : undefined;
    })
  );
}

/**
 * Given a Quote and one of its QuoteMarkets, will return time-to-live details for this quote market in milliseconds.
 *
 * @returns { maxTtl: number | undefined, ttl: number | undefined}
 * maxTtl is the market's entire TTL time. Time in milliseconds from the time the market submitted the quote, until when it expires.
 * ttl is the current-time-aware remaining TTL, based on local time.
 */
export function getQuoteMarketTtl(_quote: Quote, quoteMarket: QuoteMarket) {
  const undefinedTtl = { ttl: undefined, maxTtl: undefined };

  if (!quoteMarket.ValidUntilTime) {
    return undefinedTtl;
  }

  try {
    const validUntilTime = new Date(quoteMarket.ValidUntilTime);
    const transactTime = new Date(quoteMarket.TransactTime);

    if (!Number.isFinite(validUntilTime.getTime()) || !Number.isFinite(transactTime.getTime())) {
      return undefinedTtl;
    }

    // Total lifetime of this market quote: when the quote was created until when it expires.
    const maxTtl = differenceInMilliseconds(validUntilTime, transactTime);
    // Remaining lifetime at the component's mount time, so re-renders do not reset the countdown.
    const remainingTtl = differenceInMilliseconds(validUntilTime, new Date());

    if (!Number.isFinite(maxTtl) || maxTtl < 0 || !Number.isFinite(remainingTtl)) {
      return undefinedTtl;
    }

    // Clamp to keep the countdown label in sync with the maxTtl-based bar animation under client/server clock skew.
    const ttl = Math.min(Math.max(remainingTtl, 0), maxTtl);

    return {
      maxTtl,
      ttl,
    };
  } catch (e) {
    return undefinedTtl;
  }
}

export function getQuoteLegQuantity(
  side: SideEnum | undefined,
  legLike: { OrderQty?: string; OfferQty: string; BidQty: string }
): string;
export function getQuoteLegQuantity(
  side: SideEnum | undefined,
  legLike: { OrderQty?: string; OfferQty?: string; BidQty?: string }
): string | undefined {
  if (legLike.OrderQty != null) {
    return legLike.OrderQty;
  }

  // Temporary backwards compatibility for a release or two while older quote legs may not have OrderQty.
  return side === SideEnum.Buy
    ? legLike.OfferQty
    : side === SideEnum.Sell
      ? legLike.BidQty
      : (legLike.BidQty ?? legLike.OfferQty); // else if side is undefined we are two-way, in which case the two are the same and we use whichever
}

interface QuoteMarketDerivativeMetric {
  Value: string;
  MetricType: string; // might need some enum handling here in the future
  Currency?: string;
}

export interface QuoteMarket {
  BidAmt: string;
  BidPx: string;
  FirstTransactTime: string;
  LegIndexes?: { LegIndex: number }[];
  Market: string;
  MarketAccount: string;
  OfferAmt: string;
  Currency?: string;
  Confirm?: ConfirmStructs;
  ConfirmType?: ConfirmTypeEnum;
  BidQty?: string;
  OfferQty?: string;
  OfferPx: string;
  QuoteStatus: QuoteStatusEnum;
  TradedAmt?: string;
  TradedPx?: string;
  TradedQty?: string;
  TradedSide?: SideEnum;
  TransactTime: string;
  Text?: string;
  ValidUntilTime: string;
  LimitBidPx?: string;
  LimitOfferPx?: string;
  QID?: string;
  NegotiationStatus?: NegotiationStatusEnum;

  DerivativeMetrics?: QuoteMarketDerivativeMetric[];
}

// OMSQuote4401
export class Quote {
  static readonly rowID = 'RFQID';
  static readonly defaultColumns = [
    'SubmitTime',
    'RFQID',
    'Side',
    'Symbol',
    'OrderQty',
    'Markets',
    'SubAccount',
    'User',
    'QuoteStatus',
    'ParentRFQID',
    'QuoteID',
  ] satisfies (keyof Quote)[];

  Timestamp!: string;

  MessageID?: string;

  User!: string;

  Symbol!: string;

  // A Quote's currency can be undefined if we are buying in "units" (eg for Compound instruments)
  Currency?: string;

  get isQtyInUnits() {
    return this.Currency == null;
  }

  get units() {
    return this.isQtyInUnits ? this.OrderQty : undefined;
  }

  RFQID!: string;

  QuoteReqID!: string;

  QuoteStatus!: QuoteStatusEnum;

  QuoteID!: string;

  QuoteRequestRejectReason?: OrdRejReasonEnum;

  SubmitTime!: string;

  OrderQty?: string;

  AmountCurrency?: string;

  FirstTransactTime?: string;

  EndTime?: string;

  Side?: SideEnum;
  ExpressedOutcome?: ExpressedOutcomeEnum;

  TradedPx?: string;

  TradedQty?: string;

  TradedAmt?: string;

  TradedSide?: SideEnum;

  ParentRFQID?: string;

  /**
   * ParentOrderType is a newer property that exists on Quotes created as of backend 2.59, and _some_ records going back.
   * Specifically, only CareOrder and WorkingOrder ParentOrderTypes have been backfilled.
   */
  ParentOrderType?: ParentOrderTypeEnum;

  SweepGroupID?: string;

  TransactTime?: string;

  EventIndicator?: string;

  Markets!: QuoteMarket[];

  /** Lookup of quote market entry by market name. */
  quoteMarketsByName: Record<string, QuoteMarket>;

  Legs?: QuoteLeg[];
  legsRepresentative: QuoteLegRepresentative[];

  Text?: string;

  Group?: string;

  Parameters?: { [key: string]: string };
  NegotiationParameters?: NegotiationParameters;

  SubAccount?: string;
  Allocation?: AllocationBE;
  /** An array of all the sub accounts used. For allocs, will be the alloc members. If no allocs, will just be the sub account in an array. Else undefined. */
  subAccounts: string[] | undefined;
  /** An array of all the fixing indices referenced on this entity */
  fixingIndices: string[] | undefined;
  /** Fixing date across the quote's fixing details, if present */
  get fixingDate() {
    if (this.FixingDetails?.FixingDate) {
      return this.FixingDetails.FixingDate;
    }

    // For multileg quotes, assume all legs share the same fixing date and use the first present value.
    return Object.values(this.FixingDetails?.MultilegFixingDetails ?? {}).at(0)?.FixingDate;
  }

  OrgID?: string;

  Comments?: string;
  ExternalComments?: string;

  Aggregations?: IOMSQuote4401Aggregations[];

  PricingMode?: PricingModeEnum;

  PricingReference?: string;

  FixingDetails?: FixingDetails;

  ExternalGroup?: string;
  SettleDate?: string;

  RFQTradingProtocol?: RFQTradingProtocolEnum;

  ClearingAccount?: string;

  get isTerminal() {
    return isTerminalQuoteStatus(this.QuoteStatus);
  }

  get isPending() {
    const PENDING_QUOTE_STATUSES = [
      QuoteStatusEnum.PendingCancel,
      QuoteStatusEnum.PendingNew,
      QuoteStatusEnum.PendingFill,
    ] as const;
    return includesEnumValue(PENDING_QUOTE_STATUSES, this.QuoteStatus);
  }

  get isProbablySalesRFQ() {
    if (this.ParentOrderType != null) {
      // ParentOrderType exists as of backend 2.59, but is not backfilled. So use it if it exists, otherwise fallback.
      // SalesRFQs get CustomerOrder as their ParentOrderType
      return this.ParentOrderType === ParentOrderTypeEnum.CustomerOrder;
    }

    // For older Quotes, we stick with this old heuristic for backwards compatibility. The StF clause is here to ignore Quotes from the ETF Flow, where
    // quotes will have a ParentRFQID and be in StF, where the ParentRFQID points at a care order.
    // The backend _will backfill_ all ETF orgs in 2.59, after which we can remove this second part of the clause below.
    return this.ParentRFQID != null && this.PricingMode !== PricingModeEnum.SpreadToFixing;
  }

  /** Whether or not the user can open this active quote to see its status in the RFQ Form drawer */
  get canOpenQuote() {
    return !this.isTerminal && !this.isProbablySalesRFQ;
  }

  get tradedMarkets(): string[] {
    if (!this.Legs) {
      return [];
    }

    return uniq(this.Legs.filter(leg => toBigWithDefault(leg.TradedQty, 0).gt(0)).map(leg => leg.Market));
  }

  get tradedMarket(): QuoteMarket | undefined {
    return this.Markets.find(m => isFilledQuoteStatus(m.QuoteStatus));
  }

  get selectedMarkets(): string[] {
    return this.Markets.map(market => market.Market);
  }

  get remainQty(): string {
    return toBigWithDefault(this.OrderQty, 0)
      .minus(this.TradedQty ?? 0)
      .toFixed();
  }

  /** Returns TradedSide if it exists, otherwise Side */
  get tradedSideOrSide() {
    return this.TradedSide ?? this.Side;
  }

  /** For single-token, there will be one Leg entry per Market. For multitoken, there will be >1 (one per symbol) */
  get isMultitoken() {
    return this.Legs && this.Legs.length > this.Markets.length;
  }

  careOrder?: CareOrder;
  /** Get a new quote which is instantiated to contain a reference to the provided Care Order entity. Passing undefined will return a copy of the Quote without any Care Order. */
  enrichWithCareOrder(careOrder: CareOrder | undefined): Quote {
    return new Quote({ ...this, careOrder });
  }

  constructor(data: Partial<IOMSQuote> | Partial<Quote>) {
    Object.assign(this, data);
    this.quoteMarketsByName = Object.fromEntries((this.Markets ?? []).map(market => [market.Market, market]));
    this.subAccounts = getEntitySubAccountNames(this);
    this.fixingIndices = getFixingIndicesList(this.FixingDetails);
    this.legsRepresentative = getQuoteLegRepresentatives(this.Legs);
  }
}

interface UseQuoteColumns {
  defaultColumns?: (keyof Quote | Partial<Column>)[];
}

export function useQuoteColumns({ defaultColumns = EMPTY_ARRAY }: UseQuoteColumns): Column[] {
  const defaultVisibleColumns = useMemo(
    () =>
      new Map(
        (
          [
            {
              field: 'SubmitTime',
              type: 'date',
              sortable: true,
              initialSort: '-',
            },
            {
              field: 'RFQID',
              type: 'id',
            },
            {
              field: 'tradedSideOrSide',
              id: 'Side',
              title: 'Side',
              type: 'quoteSide',
            },
            {
              field: 'Symbol',
              type: 'security',
            },
            {
              field: 'OrderQty',
              type: 'size',
              params: { currencyField: 'Currency' },
            },
            {
              field: 'Markets',
              type: 'quoteMarkets',
            },
            {
              field: 'SubAccount',
              type: 'text',
            },
            {
              field: 'User',
              type: 'user',
            },
            {
              field: 'QuoteStatus',
              type: 'quoteStatus',
              params: {
                tooltipField: 'Text',
              } satisfies QuoteStatusColumnParams<Quote>,
            },
            {
              field: 'ParentRFQID',
              type: 'id',
            },
            {
              field: 'SweepGroupID',
              type: 'id',
              initialHide: true,
            },
            {
              field: 'ClearingAccount',
              type: 'marketAccount',
              title: 'Clearing Account',
              initialHide: true,
            },
            {
              field: 'QuoteID',
              type: 'id',
            },
            { field: 'PricingMode', type: 'text', title: 'Pricing Mode' },
          ] satisfies ColumnDef<Quote>[]
        ).map(c => [getAgGridColId(c), c])
      ),
    []
  );
  const defaultHiddenColumns = useMemo(() => {
    return new Map(
      (
        [
          {
            field: 'Timestamp',
            type: 'date',
          },
          {
            field: 'MessageID',
            type: 'text',
          },
          {
            field: 'Currency',
            type: 'currency',
          },
          {
            field: 'QuoteReqID',
            type: 'id',
          },
          {
            field: 'QuoteRequestRejectReason',
            type: 'text',
          },
          {
            field: 'AmountCurrency',
            type: 'currency',
          },
          {
            field: 'FirstTransactTime',
            type: 'date',
          },
          {
            field: 'EndTime',
            type: 'date',
          },
          {
            field: 'TradedPx',
            type: 'price',
            params: { assetField: 'Symbol' },
          },
          {
            field: 'TradedQty',
            type: 'size',
            params: { currencyField: 'Currency' },
          },
          {
            field: 'TradedAmt',
            type: 'size',
            params: { currencyField: 'AmountCurrency' },
          },
          {
            field: 'TradedSide',
            type: 'side',
          },
          { field: 'tradedMarkets', type: 'markets', title: 'Traded Markets', sortable: true },
          {
            field: 'TransactTime',
            type: 'date',
          },
          {
            field: 'EventIndicator',
            type: 'text',
          },
          {
            field: 'Legs',
            type: 'text',
          },
          {
            field: 'Text',
            type: 'text',
          },
          {
            field: 'Group',
            type: 'text',
          },
          {
            field: 'Parameters',
            type: 'text',
          },
          {
            field: 'Allocation',
            type: 'text',
          },
          {
            field: 'PricingReference',
            type: 'price',
            hide: true,
            params: {
              isReferencePrice: true,
            },
          },
          {
            field: 'fixingIndices',
            title: 'Fixing Index',
            type: 'fixingIndices',
            id: 'fixingIndices',
          },
          {
            field: 'FixingDetails',
            id: 'fixingPrice',
            title: 'Fixing Price',
            type: 'fixingPrice',
          },
          {
            field: 'fixingDate',
            title: 'Fixing Date',
            type: 'dateOnly',
            initialHide: true,
          },
        ] satisfies (false | ColumnDef<Quote>)[]
      ).map(c => [getAgGridColId(c), { ...c, initialHide: true }])
    );
  }, []);
  const columnDefinitions = useMemo(() => {
    return new Map(
      (
        [...defaultVisibleColumns.values(), ...defaultHiddenColumns.values()] satisfies ColumnDef<Quote>[] as Column[]
      ).map(c => [getAgGridColId(c), c])
    );
  }, [defaultVisibleColumns, defaultHiddenColumns]);
  return useDefaultColumns(defaultColumns, columnDefinitions);
}

/** TODO need to go over these properties and double-check which are always present and which are optional */
export interface QuoteLeg {
  Market: string;
  MarketAccount: string;
  Symbol: string;
  BidAmt?: string;
  BidQty: string;
  OrderQty?: string;
  OfferQty: string;
  OfferAmt?: string;
  BidPx: string;
  OfferPx: string;
  Currency: string;
  AmountCurrency: string;
  TransactTime: string;
  QuoteStatus: QuoteStatusEnum;
  Direction: LegDirectionEnum;
  QuoteRequestRejectReason: string;
  Text: string;
  PricingReference?: string;
  TradedPx?: string;
  TradedQty?: string;
  TradedAmt?: string;
  TradedSide?: SideEnum;

  FixingDetails?: {
    Name?: string;
    Index?: string;
    Fixing?: string;
    FixingDate?: string;
  };
}
